import {
  outputMergeSchema,
  outputMergeKey,
  outputSimilarityKey,
  type OutputMerge,
} from "./output-profile-merge";
import { packBrowserState, unpackBrowserState } from "./browser-state";
import {
  paginateProjects,
  summarizeProject,
  type ProjectPage,
  type ProjectPageQuery,
  type ProjectCatalogPage,
} from "./project-pagination";
import { migrateOutputProfiles, globalOutputSettings } from "./output-workflow";
import { reusableFormSchema, formFieldCompatible } from "./library";
import {
  principalSchema,
  scopedId,
  type AccessPrincipal,
  type CapabilityId,
} from "@proof/authorization";
import {
  tenantSettingsSchema,
  resourceRefSchema,
  parseResource,
  applyResourceSnapshot,
  type Resource,
  type TenantSettings,
  type ResourceRef,
  type ResourceQuery,
  filterResources,
} from "./catalog";
import type { OutputProfile, ResourceKind } from "./schema";
import {
  type Project,
  type Values,
  parseProject,
  uid,
  datasetSchema,
} from "./schema";
export interface Revision {
  id: string;
  projectId: string;
  revision: number;
  at: string;
  actor: string;
  reason: string;
  definition: Project;
  manualLocked?: boolean;
  publicationLockedUntil?: string;
}
export interface PublishedVersion {
  id: string;
  projectId: string;
  version: number;
  at: string;
  actor?: string;
  definition: Project;
}
export interface ProofRecord {
  sourceParameters?: import("./schema").SourceParameters;
  /** Chosen global profile per selector ("top" or "section:<id>"). */
  profileSelections?: Record<string, string>;
  contextHash?: string;
  tenantId?: string;
  /** Assigned by the authenticated host. Absent on older/offline records. */
  ownerSubjectId?: string;
  id: string;
  projectId: string;
  partId?: string;
  name: string;
  values: Values;
  comments: string;
  accepted: boolean;
  initials: string;
  at: string;
  versionId?: string;
}
export type { Resource } from "./catalog";
/** Bounded copy of saved profile selections; drops anything malformed. */
export function cleanProfileSelections(
  input: unknown,
): Record<string, string> | undefined {
  if (!input || typeof input !== "object" || Array.isArray(input)) return;
  const rows = Object.entries(input as Record<string, unknown>)
    .filter(
      ([key, value]) =>
        /^(top|top:.{1,200}|section:.{1,200})$/.test(key) &&
        typeof value === "string" &&
        value.length > 0 &&
        value.length <= 300,
    )
    .slice(0, 50) as [string, string][];
  return rows.length ? Object.fromEntries(rows) : undefined;
}
export interface Repository {
  /** Native host optimization for new isolated-import drafts; retains reusable presets atomically. */
  importDraft?(
    tenantId: string,
    project: Project,
    actor: string,
  ): Promise<Project>;
  catalog?(
    tenantId: string,
    query?: ProjectPageQuery,
    signal?: AbortSignal,
  ): Promise<ProjectCatalogPage>;
  /** Release adapter-owned connections during host shutdown. */
  close?(): Promise<void>;
  checkHealth?(): Promise<void>;
  list(tenantId: string): Promise<Project[]>;
  page?(
    tenantId: string,
    query?: ProjectPageQuery,
    signal?: AbortSignal,
  ): Promise<ProjectPage>;
  get(tenantId: string, id: string): Promise<Project | undefined>;
  save(
    tenantId: string,
    project: Project,
    expectedRevision: number,
    actor?: string,
    reason?: string,
  ): Promise<Project>;
  create(
    tenantId: string,
    definition: unknown,
    options?: CreationOptions,
  ): Promise<Project>;
  remove(tenantId: string, id: string): Promise<void>;
  /** Server-side usage tracking; remote clients cannot clear this marker. */
  markUsed?(tenantId: string, id: string): Promise<void>;
  history(tenantId: string, id: string): Promise<Revision[]>;
  restore(
    tenantId: string,
    id: string,
    revisionId: string,
    expected: number | null,
    actor?: string,
  ): Promise<Project>;
  lockRevision(
    tenantId: string,
    id: string,
    revisionId: string,
    locked: boolean,
  ): Promise<Revision | undefined>;
  tenantSettings(tenantId: string): Promise<TenantSettings>;
  saveTenantSettings(
    tenantId: string,
    settings: TenantSettings,
    expectedRevision: number,
  ): Promise<TenantSettings>;
  publish(
    tenantId: string,
    id: string,
    actor?: string,
  ): Promise<PublishedVersion>;
  versions(tenantId: string, id: string): Promise<PublishedVersion[]>;
  saveProof(tenantId: string, proof: ProofRecord): Promise<ProofRecord>;
  proofs(
    tenantId: string,
    id: string,
    ownerSubjectId?: string,
  ): Promise<ProofRecord[]>;
  resources(tenantId: string, query?: ResourceQuery): Promise<Resource[]>;
  saveResource(
    tenantId: string,
    resource: Resource,
    expectedVersion?: number,
    actor?: string,
  ): Promise<Resource>;
  mergeOutputProfiles?(
    tenantId: string,
    input: OutputMerge,
  ): Promise<{ archived: string[] }>;
  archiveResource(
    tenantId: string,
    id: string,
    archived: boolean,
  ): Promise<void>;
}
export interface CreationOptions {
  resources?: Partial<Record<ResourceKind, ResourceRef>>;
  useDefaults?: boolean;
  actor?: string;
  validate?: (project: Project) => Promise<unknown>;
}
export class ConflictError extends Error {
  constructor() {
    super("This record changed since it was loaded. Reload before saving.");
  }
}
export interface StoreState {
  revisionCounters?: {
    tenantId: string;
    projectId: string;
    revision: number;
  }[];
  projects: Project[];
  history: Revision[];
  published: PublishedVersion[];
  proofs: ProofRecord[];
  resources: Resource[];
  tenantSettings?: TenantSettings[];
  resourceStates?: { tenantId: string; id: string; archived: boolean }[];
}
export interface RepositoryOptions {
  now?: () => Date;
}
export class MemoryRepository implements Repository {
  async catalog(tenant: string, query: ProjectPageQuery = {}) {
    await this.readable();
    return paginateProjects(
      this.state.projects
        .filter((project) => project.tenantId === tenant)
        .map(summarizeProject),
      query,
    );
  }
  protected state: StoreState;
  private mutations: Promise<void> = Promise.resolve();
  constructor(
    seed: Project[] = [],
    private options: RepositoryOptions = {},
  ) {
    this.state = {
      projects: seed.map(parseProject),
      history: [],
      published: [],
      proofs: [],
      resources: [],
      tenantSettings: [],
      resourceStates: [],
    };
    this.state.revisionCounters = [
      ...new Map(
        this.state.projects.map((p) => [
          JSON.stringify([p.tenantId, p.id]),
          { tenantId: p.tenantId, projectId: p.id, revision: p.revision },
        ]),
      ).values(),
    ];
  }
  protected async changed(): Promise<void> {}
  protected async transaction<T>(operation: () => Promise<T>): Promise<T> {
    return operation();
  }
  protected async readable(): Promise<void> {
    await this.mutations;
  }
  private now() {
    return (this.options.now?.() || new Date()).toISOString();
  }
  private async mutate<T>(operation: () => T | Promise<T>): Promise<T> {
    const next = this.mutations.then(() =>
      this.transaction(async () => {
        const before = structuredClone(this.state);
        try {
          const result = await operation();
          await this.changed();
          return structuredClone(result);
        } catch (error) {
          this.state = before;
          throw error;
        }
      }),
    );
    this.mutations = next.then(
      () => {},
      () => {},
    );
    return next;
  }
  private settings(tenant: string) {
    return (
      this.state.tenantSettings?.find((s) => s.tenantId === tenant) ||
      tenantSettingsSchema.parse({ tenantId: tenant })
    );
  }
  private prune(tenant: string) {
    const now = Date.parse(this.now()),
      quota = this.settings(tenant).revisionQuota;
    const unlocked = this.state.history
      .filter(
        (h) =>
          h.definition.tenantId === tenant &&
          !h.manualLocked &&
          !(
            h.publicationLockedUntil &&
            Date.parse(h.publicationLockedUntil) > now
          ),
      )
      .sort((a, b) => a.at.localeCompare(b.at) || a.id.localeCompare(b.id));
    const remove = new Set(
      unlocked.slice(0, Math.max(0, unlocked.length - quota)).map((h) => h.id),
    );
    this.state.history = this.state.history.filter((h) => !remove.has(h.id));
  }
  private archive(
    project: Project,
    actor: string,
    reason: string,
    publicationUntil?: string,
  ): Revision {
    // One immutable source envelope per optimistic revision; publication changes only lock metadata.
    const existing = this.state.history.find(
      (h) =>
        h.projectId === project.id &&
        h.definition.tenantId === project.tenantId &&
        h.revision === project.revision,
    );
    if (existing) {
      if (
        publicationUntil &&
        (!existing.publicationLockedUntil ||
          existing.publicationLockedUntil < publicationUntil)
      )
        existing.publicationLockedUntil = publicationUntil;
      return existing;
    }
    const revision: Revision = {
      id: uid("revision"),
      projectId: project.id,
      revision: project.revision,
      at: this.now(),
      actor,
      reason,
      manualLocked: false,
      definition: structuredClone(project),
      ...(publicationUntil ? { publicationLockedUntil: publicationUntil } : {}),
    };
    this.state.history.push(revision);
    return revision;
  }
  private applySave(
    tenant: string,
    project: Project,
    expected: number | null,
    actor: string,
    reason: string,
  ): Project {
    const p = migrateOutputProfiles(parseProject(project));
    if (p.tenantId !== tenant) throw new Error("Tenant mismatch");
    const current = this.state.projects.find(
      (x) => x.id === p.id && x.tenantId === tenant,
    );
    if (
      current
        ? current.revision !== expected
        : expected !== 0 && expected !== null
    )
      throw new ConflictError();
    const snapshots = [
      current,
      ...this.state.history
        .filter((h) => h.projectId === p.id && h.definition.tenantId === tenant)
        .map((h) => h.definition),
      ...this.state.published
        .filter((v) => v.projectId === p.id && v.definition.tenantId === tenant)
        .map((v) => v.definition),
    ].filter((s): s is Project => !!s);
    const known = new Map<string, string>();
    for (const snapshot of snapshots)
      for (const raw of snapshot.datasets) {
        const dataset = datasetSchema.parse(raw);
        known.set(
          JSON.stringify([dataset.id, dataset.version]),
          JSON.stringify(dataset),
        );
      }
    for (const dataset of p.datasets) {
      const existing = known.get(JSON.stringify([dataset.id, dataset.version]));
      if (existing && existing !== JSON.stringify(dataset))
        throw new Error(
          "Cannot change an immutable dataset version; publish a new version",
        );
    }
    if (current) this.archive(current, actor, reason);
    const historical = Math.max(
      0,
      ...this.state.history
        .filter((h) => h.projectId === p.id && h.definition.tenantId === tenant)
        .map((h) => h.revision),
      ...this.state.published
        .filter((v) => v.projectId === p.id && v.definition.tenantId === tenant)
        .map((v) => v.definition.revision),
    );
    const counter = this.state.revisionCounters?.find(
      (r) => r.tenantId === tenant && r.projectId === p.id,
    );
    p.revision =
      Math.max(current?.revision || 0, historical, counter?.revision || 0) + 1;
    this.state.revisionCounters = [
      ...(this.state.revisionCounters || []).filter(
        (r) => r.tenantId !== tenant || r.projectId !== p.id,
      ),
      { tenantId: tenant, projectId: p.id, revision: p.revision },
    ];
    p.status = p.status === "archived" ? "archived" : "draft";
    p.usedAt = current?.usedAt;
    this.state.projects = this.state.projects.filter(
      (x) => !(x.id === p.id && x.tenantId === tenant),
    );
    this.state.projects.push(p);
    this.prune(tenant);
    return p;
  }
  async list(tenant: string) {
    await this.readable();
    return structuredClone(
      this.state.projects.filter((p) => p.tenantId === tenant),
    );
  }
  async page(tenant: string, query: ProjectPageQuery = {}) {
    await this.readable();
    return paginateProjects(
      this.state.projects.filter((project) => project.tenantId === tenant),
      query,
    );
  }
  async get(tenant: string, id: string) {
    await this.readable();
    return structuredClone(
      this.state.projects.find((p) => p.id === id && p.tenantId === tenant),
    );
  }
  async save(
    tenant: string,
    project: Project,
    expected: number,
    actor = "demo",
    reason = "Saved draft",
  ) {
    return this.mutate(() =>
      this.applySave(tenant, project, expected, actor, reason),
    );
  }
  async create(
    tenant: string,
    definition: unknown,
    options: CreationOptions = {},
  ) {
    return this.mutate(async () => {
      if (!definition || typeof definition !== "object")
        throw new Error("Document definition is required");
      const raw = definition as Record<string, unknown>;
      if (raw.tenantId && raw.tenantId !== tenant)
        throw new Error("Tenant mismatch");
      let p = parseProject({
        ...raw,
        id: uid("project"),
        tenantId: tenant,
        revision: 0,
        status: "draft",
      });
      const selections = {
        ...(options.useDefaults === false
          ? {}
          : this.settings(tenant).defaultResources),
        ...tenantSettingsSchema.shape.defaultResources.parse(
          options.resources || {},
        ),
      };
      const order: ResourceKind[] = [
        "page-size",
        "production",
        "font",
        "style",
        "dataset",
        "form",
        "rule",
        "preset",
        "validation",
        "format",
        "output",
        "sheet",
      ];
      for (const kind of order) {
        const selection = selections[kind];
        if (!selection) continue;
        const ref = resourceRefSchema.parse(selection),
          r = this.state.resources.find(
            (r) =>
              r.id === ref.id &&
              r.version === ref.version &&
              r.tenantId === tenant &&
              r.kind === kind,
          );
        if (
          !r ||
          this.state.resourceStates?.some(
            (s) => s.tenantId === tenant && s.id === r.id && s.archived,
          )
        )
          throw new Error(`Resource version is unavailable: ${kind}`);
        p = applyResourceSnapshot(p, r);
        if (kind === "style") {
          const styleId = p.styles.at(-1)!.id;
          p.documentSettings.defaultStyleId = styleId;
          p.pages = p.pages.map((pg) => ({
            ...pg,
            nodes: pg.nodes.map((n) =>
              ["text", "richtext", "calendar"].includes(n.type) && !n.styleId
                ? { ...n, styleId, typographyMode: "inherit" }
                : n,
            ),
          }));
        }
      }
      await options.validate?.(p);
      return this.applySave(
        tenant,
        p,
        0,
        options.actor || "demo",
        "Created document",
      );
    });
  }
  async markUsed(tenant: string, id: string) {
    return this.mutate(() => {
      const p = this.state.projects.find(
        (p) => p.tenantId === tenant && p.id === id,
      );
      if (!p) throw new Error("Project not found");
      if (p.status === "archived")
        throw new Error("Restore the archived project before using it");
      p.usedAt ||= this.now();
    });
  }
  async remove(tenant: string, id: string) {
    return this.mutate(() => {
      const current = this.state.projects.find(
        (p) => p.tenantId === tenant && p.id === id,
      );
      const published = this.state.published.some(
        (v) => v.projectId === id && v.definition.tenantId === tenant,
      );
      const proof = this.state.proofs.some(
        (p) => p.projectId === id && p.tenantId === tenant,
      );
      const historicallyUsed = this.state.history.some(
        (h) =>
          h.projectId === id &&
          h.definition.tenantId === tenant &&
          (h.definition.usedAt || h.definition.status === "published"),
      );
      if (
        current?.usedAt ||
        current?.status === "published" ||
        published ||
        proof ||
        historicallyUsed
      )
        throw new Error(
          "This project has been used and cannot be deleted. Archive it instead.",
        );
      if (current) this.archive(current, "demo", "Removed unused project");
      this.state.projects = this.state.projects.filter(
        (p) => !(p.tenantId === tenant && p.id === id),
      );
      this.prune(tenant);
    });
  }
  async history(tenant: string, id: string) {
    await this.readable();
    return structuredClone(
      this.state.history
        .filter((h) => h.projectId === id && h.definition.tenantId === tenant)
        .sort((a, b) => b.at.localeCompare(a.at) || b.id.localeCompare(a.id)),
    );
  }
  async restore(
    tenant: string,
    id: string,
    revisionId: string,
    expected: number | null,
    actor = "demo",
  ) {
    return this.mutate(() => {
      const h = this.state.history.find(
        (h) =>
          h.id === revisionId &&
          h.projectId === id &&
          h.definition.tenantId === tenant,
      );
      if (!h) throw new Error("Revision not found");
      const current = this.state.projects.find(
        (p) => p.id === id && p.tenantId === tenant,
      );
      if (!current && expected !== null) throw new ConflictError();
      // Restore bindings/content while retaining later immutable data versions.
      const restored = structuredClone(h.definition);
      for (const dataset of current?.datasets || [])
        if (
          !restored.datasets.some(
            (d) => d.id === dataset.id && d.version === dataset.version,
          )
        )
          restored.datasets.push(structuredClone(dataset));
      return this.applySave(
        tenant,
        restored,
        expected,
        actor,
        `Restored revision ${h.revision}`,
      );
    });
  }
  async lockRevision(
    tenant: string,
    id: string,
    revisionId: string,
    locked: boolean,
  ) {
    return this.mutate(() => {
      const h = this.state.history.find(
        (h) =>
          h.id === revisionId &&
          h.projectId === id &&
          h.definition.tenantId === tenant,
      );
      if (!h) throw new Error("Revision not found");
      h.manualLocked = locked;
      this.prune(tenant);
      return this.state.history.find((r) => r.id === h.id);
    });
  }
  async tenantSettings(tenant: string) {
    await this.readable();
    return structuredClone(this.settings(tenant));
  }
  async saveTenantSettings(
    tenant: string,
    value: TenantSettings,
    expected: number,
  ) {
    return this.mutate(() => {
      const settings = tenantSettingsSchema.parse(value);
      if (settings.tenantId !== tenant) throw new Error("Tenant mismatch");
      if (this.settings(tenant).revision !== expected)
        throw new ConflictError();
      for (const [kind, ref] of Object.entries(settings.defaultResources))
        if (ref) {
          const r = this.state.resources.find(
            (r) =>
              r.id === ref.id &&
              r.version === ref.version &&
              r.kind === kind &&
              r.tenantId === tenant,
          );
          if (
            !r ||
            this.state.resourceStates?.some(
              (s) => s.tenantId === tenant && s.id === r.id && s.archived,
            )
          )
            throw new Error("Default resource version is unavailable");
          if (r.projectId)
            throw new Error("Tenant defaults require a shared resource");
        }
      settings.revision = expected + 1;
      this.state.tenantSettings = [
        ...(this.state.tenantSettings || []).filter(
          (s) => s.tenantId !== tenant,
        ),
        settings,
      ];
      this.prune(tenant);
      return settings;
    });
  }
  async publish(tenant: string, id: string, actor = "demo") {
    return this.mutate(() => {
      const p = this.state.projects.find(
        (p) => p.id === id && p.tenantId === tenant,
      );
      if (!p) throw new Error("Project not found");
      if (p.status === "archived")
        throw new Error("Restore the archived project before publishing");
      parseProject(p);
      const at = this.now();
      p.usedAt ||= at;
      this.archive(
        p,
        actor,
        "Published source draft",
        new Date(Date.parse(at) + 14 * 86400000).toISOString(),
      );
      const version: PublishedVersion = {
        id: uid("version"),
        projectId: id,
        version:
          this.state.published.filter(
            (v) => v.projectId === id && v.definition.tenantId === tenant,
          ).length + 1,
        at,
        actor,
        definition: { ...structuredClone(p), status: "published" },
      };
      this.state.published.push(version);
      p.status = "published";
      this.prune(tenant);
      return version;
    });
  }
  async versions(tenant: string, id: string) {
    await this.readable();
    return structuredClone(
      this.state.published.filter(
        (v) => v.projectId === id && v.definition.tenantId === tenant,
      ),
    );
  }
  async saveProof(tenant: string, proof: ProofRecord) {
    return this.mutate(() => {
      if (proof.ownerSubjectId !== undefined)
        scopedId.parse(proof.ownerSubjectId);
      if (
        !this.state.projects.some(
          (p) => p.id === proof.projectId && p.tenantId === tenant,
        )
      )
        throw new Error("Project not found");
      if (
        this.state.proofs.some(
          (p) =>
            p.tenantId === tenant &&
            p.id === proof.id &&
            p.projectId !== proof.projectId,
        )
      )
        throw new Error("Proof identity belongs to another project");
      const existing = this.state.proofs.find(
        (p) => p.tenantId === tenant && p.id === proof.id,
      );
      if (existing && existing.ownerSubjectId !== proof.ownerSubjectId)
        throw new Error("Proof ownership cannot change");
      this.state.proofs = this.state.proofs.filter(
        (p) => !(p.id === proof.id && p.tenantId === tenant),
      );
      if (proof.tenantId && proof.tenantId !== tenant)
        throw new Error("Tenant mismatch");
      const project = this.state.projects.find(
        (p) => p.id === proof.projectId && p.tenantId === tenant,
      )!;
      if (project.status === "archived")
        throw new Error(
          "Restore the archived project before submitting proofs",
        );
      project.usedAt ||= this.now();
      proof = { ...proof, tenantId: tenant };
      this.state.proofs.push(structuredClone(proof));
      this.prune(tenant);
      return proof;
    });
  }
  async proofs(tenant: string, id: string, ownerSubjectId?: string) {
    if (ownerSubjectId !== undefined) scopedId.parse(ownerSubjectId);
    await this.readable();
    if (!this.state.projects.some((p) => p.id === id && p.tenantId === tenant))
      return [];
    return structuredClone(
      this.state.proofs.filter(
        (p) =>
          p.projectId === id &&
          p.tenantId === tenant &&
          (ownerSubjectId === undefined || p.ownerSubjectId === ownerSubjectId),
      ),
    );
  }
  async resources(tenant: string, query?: ResourceQuery) {
    await this.readable();
    return structuredClone(
      filterResources(
        this.state.resources
          .filter((r) => r.tenantId === tenant)
          .map((r) => ({
            ...r,
            archived:
              this.state.resourceStates?.some(
                (s) => s.tenantId === tenant && s.id === r.id && s.archived,
              ) || false,
          })),
        query,
      ),
    );
  }
  async saveResource(
    tenant: string,
    resource: Resource,
    expected = resource.version,
    actor = "demo",
  ) {
    return this.mutate(() => {
      const r = parseResource(resource);
      if (r.tenantId !== tenant) throw new Error("Tenant mismatch");
      if (
        r.kind === "form" &&
        r.projectId &&
        reusableFormSchema.parse(r.value).purpose === "profile-collection"
      )
        throw new Error(
          "Global profile collections cannot belong to a project",
        );
      if (r.kind === "output" && !r.projectId)
        r.value = globalOutputSettings(r.value as Project["outputs"][number]);
      const own = this.state.resources.filter(
          (v) => v.id === r.id && v.tenantId === tenant,
        ),
        latest = Math.max(0, ...own.map((r) => r.version));
      if (r.kind === "output" && r.projectId && !own.length)
        throw new Error(
          "Output profiles are global; assign them to project output stages",
        );
      if (latest !== expected) throw new ConflictError();
      if (own.some((v) => v.kind !== r.kind))
        throw new Error("Resource kind cannot change");
      if (own.some((v) => v.projectId !== r.projectId))
        throw new Error(
          "Resource project cannot change; create a separate copy instead",
        );
      if (
        this.state.resourceStates?.some(
          (s) => s.tenantId === tenant && s.id === r.id && s.archived,
        )
      )
        throw new Error("Resource is archived");
      const next = { ...r, version: latest + 1, at: this.now(), actor };
      this.state.resources.push(next);
      if (r.kind === "form" && !r.projectId) {
        const definition = reusableFormSchema.parse(next.value);
        const links = definition.extra_json_data?.linkedProjectIds || [];
        const linked = links.map((id) =>
          this.state.projects.find(
            (project) => project.tenantId === tenant && project.id === id,
          ),
        );
        if (linked.some((project) => !project || project.status === "archived"))
          throw new Error(
            "A linked source project is unavailable; remove its link before saving",
          );
        if (latest > 0)
          for (const project of linked) {
            const fields = project!.fields.map((target) => {
              const input = definition.fields.find(
                (input) => input.key === target.key,
              );
              if (!input) return target;
              if (!formFieldCompatible(input, target))
                throw new Error(
                  `Linked variable ${target.key} changed type; review its mapping first`,
                );
              return {
                ...target,
                label: input.label,
                help: input.help,
                required: input.required,
                choices: input.choices,
                validation: input.validation,
                format: input.format,
                default:
                  target.calculation || target.source.kind !== "literal"
                    ? target.default
                    : input.default,
                image: input.image,
              };
            });
            if (JSON.stringify(fields) !== JSON.stringify(project!.fields)) {
              const reason = `Updated linked form ${next.name} v${next.version}`;
              const updated = this.applySave(
                tenant,
                { ...project!, fields },
                project!.revision,
                actor,
                reason,
              );
              this.archive(updated, actor, reason);
            }
          }
      }
      this.prune(tenant);
      return next;
    });
  }
  async mergeOutputProfiles(tenant: string, raw: OutputMerge) {
    const input = outputMergeSchema.parse(raw);
    return this.mutate(() => {
      const seen = new Set<string>(),
        archived: string[] = [],
        updates: Resource[] = [];
      const defaults = new Set(
        Object.values(this.settings(tenant).defaultResources).map(
          (ref) => ref?.id,
        ),
      );
      const read = (ref: { id: string; version: number }) => {
        if (seen.has(ref.id))
          throw new Error("A profile may only occur once in a merge");
        seen.add(ref.id);
        const latest = this.state.resources
          .filter((row) => row.tenantId === tenant && row.id === ref.id)
          .sort((a, b) => b.version - a.version)[0];
        if (
          !latest ||
          latest.kind !== "output" ||
          latest.projectId ||
          this.state.resourceStates?.some(
            (row) =>
              row.tenantId === tenant && row.id === ref.id && row.archived,
          )
        )
          throw new Error("Choose active shared output profiles");
        if (latest.version !== ref.version) throw new ConflictError();
        return latest;
      };
      for (const group of input.groups) {
        const keep = read(group.keep),
          key = group.merged ? outputSimilarityKey(keep) : outputMergeKey(keep);
        for (const ref of group.duplicates) {
          const duplicate = read(ref);
          if (defaults.has(duplicate.id))
            throw new Error("Keep the default profile as the survivor");
          if (
            (group.merged
              ? outputSimilarityKey(duplicate)
              : outputMergeKey(duplicate)) !== key
          )
            throw new Error(
              "Output profile settings differ; reload the matching profiles",
            );
          archived.push(duplicate.id);
        }
        if (group.merged) {
          const value = {
            ...group.merged,
            id: (keep.value as OutputProfile).id,
            name: (keep.value as OutputProfile).name,
            outputPreset: undefined,
          };
          if (outputSimilarityKey({ ...keep, value }) !== key)
            throw new Error(
              "Merged settings must retain the group's format, color space, role and linked profiles",
            );
          if (outputMergeKey({ ...keep, value }) !== outputMergeKey(keep))
            updates.push({
              ...keep,
              version: keep.version + 1,
              value,
              at: this.now(),
            });
        }
      }
      this.state.resources.push(...updates);
      this.state.resourceStates = [
        ...(this.state.resourceStates || []).filter(
          (row) => row.tenantId !== tenant || !archived.includes(row.id),
        ),
        ...archived.map((id) => ({ tenantId: tenant, id, archived: true })),
      ];
      return { archived };
    });
  }
  async archiveResource(tenant: string, id: string, archived: boolean) {
    return this.mutate(() => {
      if (
        !this.state.resources.some((r) => r.tenantId === tenant && r.id === id)
      )
        throw new Error("Resource not found");
      if (
        archived &&
        Object.values(this.settings(tenant).defaultResources).some(
          (ref) => ref?.id === id,
        )
      )
        throw new Error(
          "Unset the tenant default before archiving this resource",
        );
      this.state.resourceStates = [
        ...(this.state.resourceStates || []).filter(
          (s) => !(s.tenantId === tenant && s.id === id),
        ),
        { tenantId: tenant, id, archived },
      ];
      this.prune(tenant);
    });
  }
  exportState() {
    return structuredClone(this.state);
  }
  importState(input: StoreState) {
    const state = structuredClone(input);
    const counters = new Map<
      string,
      { tenantId: string; projectId: string; revision: number }
    >();
    for (const row of state.revisionCounters || []) {
      if (
        !row ||
        typeof row.tenantId !== "string" ||
        !row.tenantId ||
        typeof row.projectId !== "string" ||
        !row.projectId ||
        !Number.isSafeInteger(row.revision) ||
        row.revision < 0
      )
        throw new Error("Invalid revision counter");
      const key = JSON.stringify([row.tenantId, row.projectId]);
      if (counters.has(key)) throw new Error("Duplicate revision counter");
      counters.set(key, row);
    }
    state.projects = state.projects.map(parseProject);
    state.history = state.history.map((r) => ({
      ...r,
      manualLocked: r.manualLocked || false,
      definition: parseProject(r.definition),
    }));
    state.published = state.published.map((v) => ({
      ...v,
      definition: parseProject(v.definition),
    }));
    for (const project of [
      ...state.projects,
      ...state.history.map((h) => h.definition),
      ...state.published.map((v) => v.definition),
    ]) {
      const key = JSON.stringify([project.tenantId, project.id]),
        old = counters.get(key);
      if (!old || old.revision < project.revision)
        counters.set(key, {
          tenantId: project.tenantId,
          projectId: project.id,
          revision: project.revision,
        });
    }
    state.revisionCounters = [...counters.values()];
    state.tenantSettings = (state.tenantSettings || []).map((s) =>
      tenantSettingsSchema.parse(s),
    );
    state.resourceStates = state.resourceStates || [];
    state.resources = state.resources.map((r) => parseResource(r));
    for (const h of state.history) {
      const protections = state.published
        .filter(
          (v) =>
            v.projectId === h.projectId &&
            v.definition.tenantId === h.definition.tenantId &&
            v.definition.revision === h.revision,
        )
        .map((v) => new Date(Date.parse(v.at) + 14 * 86400000).toISOString());
      if (protections.length)
        h.publicationLockedUntil = [
          h.publicationLockedUntil || "",
          ...protections,
        ]
          .sort()
          .at(-1);
    }
    state.proofs = state.proofs.map((proof) => {
      if (proof.ownerSubjectId !== undefined)
        scopedId.parse(proof.ownerSubjectId);
      if (proof.tenantId) return proof;
      const owners = [
        ...new Set(
          state.projects
            .filter((p) => p.id === proof.projectId)
            .map((p) => p.tenantId),
        ),
      ];
      if (owners.length !== 1)
        throw new Error("Legacy proof tenant identity is ambiguous");
      return { ...proof, tenantId: owners[0] };
    });
    this.state = state;
  }
}
export class BrowserRepository extends MemoryRepository {
  private stored: string | null = null;
  constructor(
    private key = "proof-studio-v1",
    seed: Project[] = [],
  ) {
    super(seed);
    if (typeof localStorage !== "undefined") {
      try {
        this.reload();
      } catch {
        /* Preserve incompatible data for user recovery. */
      }
    }
  }
  private reload() {
    const raw = localStorage.getItem(this.key);
    if (raw !== null && raw !== this.stored)
      this.importState(unpackBrowserState(raw));
    this.stored = raw;
  }
  protected async readable() {
    await super.readable();
    this.reload();
  }
  protected async transaction<T>(operation: () => Promise<T>): Promise<T> {
    const run = async () => {
      this.reload();
      return operation();
    };
    // Shared tabs serialize workspace writes where Web Locks are available.
    return typeof navigator !== "undefined" && navigator.locks
      ? await navigator.locks.request(`proof-repository:${this.key}`, run)
      : run();
  }
  /** Replace the whole workspace with a validated backup, in one write. */
  async restoreState(input: StoreState) {
    // Validate on a scratch repository first so a bad backup changes nothing.
    new MemoryRepository([]).importState(input);
    await this.transaction(async () => {
      this.importState(input);
      await this.changed();
    });
  }
  protected async changed() {
    if (localStorage.getItem(this.key) !== this.stored)
      throw new ConflictError();
    const raw = packBrowserState(this.exportState());
    localStorage.setItem(this.key, raw);
    this.stored = raw;
  }
}
export const capabilities = {
  editor: [
    "documents.view",
    "documents.edit",
    "resources.view",
    "proofs.view",
    "help.view",
    "fonts.view",
  ],
  tester: [
    "documents.view",
    "proofs.view",
    "proofs.generate",
    "help.view",
    "fonts.view",
  ],
  assetManager: [
    "assets.manage",
    "resources.manage",
    "resources.view",
    "help.view",
    "fonts.view",
    "fonts.manage",
  ],
  administrator: ["*"],
} as const;
export type Principal = AccessPrincipal;
const capabilityAliases: Record<string, CapabilityId[]> = {
  "documents.view": ["tenant.documents.view"],
  "documents.edit": ["tenant.documents.manage"],
  "documents.history.manage": ["tenant.setup.manage"],
  "documents.publish": ["tenant.templates.publish"],
  "proofs.view": ["tenant.proofs.use"],
  "proofs.generate": ["tenant.proofs.generate"],
  "assets.use": ["tenant.assets.use"],
  "assets.manage": ["tenant.assets.manage"],
  "resources.view": [
    "tenant.documents.view",
    "tenant.proofs.use",
    "tenant.resources.manage",
  ],
  "resources.manage": ["tenant.resources.manage"],
  "fonts.view": ["tenant.assets.use", "tenant.fonts.manage"],
  "fonts.manage": ["tenant.fonts.manage"],
  "setup.manage": ["tenant.setup.manage"],
  "tenant.settings.manage": ["tenant.setup.manage"],
  "users.manage": ["tenant.users.manage"],
  "roles.manage": ["tenant.roles.manage"],
  "audit.view": ["tenant.audit.view"],
  "help.view": ["tenant.help.view"],
};
export function authorize(
  principal: Principal,
  tenant: string,
  capability: string,
) {
  const valid = principalSchema.safeParse(principal);
  if (!valid.success || principal.tenantId !== tenant)
    throw new Error("Forbidden");
  if (principal.authorization) {
    if (
      !(capabilityAliases[capability] || []).some((c) =>
        principal.authorization!.capabilities.includes(c),
      )
    )
      throw new Error("Forbidden");
    return;
  }
  if (
    principal.tenantId !== tenant ||
    !principal.roles.some((role) =>
      capabilities[role].some((c) => c === "*" || c === capability),
    )
  )
    throw new Error("Forbidden");
}
